import arrayMutators from 'final-form-arrays';
import React, { useCallback, useMemo, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button, Checkbox, Pill } from '@xsolla/ui-core';
import { Modal } from '@xsolla/ui-modal';

import { updateOAuthAuth } from '../../../api';
import { ClientTypes, OauthSettings } from '../../../api/configurations/oauth2/types';
import { PROJECT_LINKS } from '../../../constants';
import { getOAuthType } from '../../../utils';
import {
  CollapseWithTitle,
  CopyHiddenInput,
  FieldInput,
  FieldInputNumber,
  FormFooter,
  LinkFieldArray,
  LinkPAStyle,
  RadioGroup
} from '../../ui';
import {
  CommonFont,
  Header,
  HeavyFont,
  ModalContentBox,
  ModalContentBoxToken,
  ModalHeaderWrapper,
  RemoveButtonBlock,
  RemoveButtonSubtitle,
  TokenExtra
} from './style';
import { grantTypes } from './utils';
import { clientNameMaxLength, FormValues, Oauth2ModalValidator } from './validator';

type Props = {
  onClose: () => void;
  openRemoveModal: () => void;
  oauthSettings: OauthSettings;
  merchantId: string;
  setOauthSettings: React.Dispatch<React.SetStateAction<OauthSettings[]>>;
};

export const UpdateOauthModal = ({
  onClose,
  openRemoveModal,
  setOauthSettings,
  merchantId,
  oauthSettings
}: Props): JSX.Element => {
  const intl = useIntl();
  const { OAUTH2_WIDGET, OAUTH2API } = PROJECT_LINKS;

  const [fetching, setFetching] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [agreeLongToken, setAgreeLongToken] = useState<boolean>(false);

  const handleChangeCheck = useCallback(() => {
    setAgreeLongToken(!agreeLongToken);
  }, [agreeLongToken]);

  const oAuthTypeMemo = useMemo(
    () => getOAuthType(oauthSettings.grant_types, oauthSettings.is_public),
    []
  );

  const onSubmit = async (values: FormValues) => {
    const isServer = values.is_public === ClientTypes.server;
    const isPublic = values.is_public === ClientTypes.public;
    const oauthClientAccess = isServer
      ? [
          {
            resource: merchantId,
            resource_name: 'publisher_id'
          }
        ]
      : [];

    try {
      setFetching(true);
      setError(false);
      const updatedClient = {
        ...oauthSettings,
        ...values,
        access: oauthClientAccess,
        grant_types: grantTypes(isServer, oauthSettings.grant_types),
        client_id: oauthSettings.id as number,
        is_public: isPublic,
        lifetime: Number(values.lifetime),
        redirect_uris: oAuthTypeMemo === 'server' ? [] : values.redirect_uris
      };

      await updateOAuthAuth(oauthSettings.id as number, updatedClient);
      await setOauthSettings((prev) => {
        return prev.map((item) => {
          if (item.id === oauthSettings.id) {
            return updatedClient;
          }
          return item;
        });
      });
      onClose();
    } catch (err) {
      setError(true);
    } finally {
      setFetching(false);
    }
  };

  return (
    <Modal onClose={onClose} size="md">
      <ModalHeaderWrapper>
        <Header>{intl.messages['authorization.settings.oauth2.modal.connect.title']}</Header>
        <Pill palette="action">{intl.messages['social-connection.select.connected']}</Pill>
      </ModalHeaderWrapper>
      <ModalContentBox>
        <CollapseWithTitle
          title={intl.messages['authorization.settings.oauth2.modal.connect.instruction.title']}
          instruction={intl.formatMessage(
            {
              id: 'authorization.settings.oauth2.modal.connect.instruction'
            },
            {
              br: () => <br />,
              a: (chunk: string) => (
                <LinkPAStyle
                  href={OAUTH2_WIDGET}
                  target="_blank"
                  rel="noreferrer"
                  data-test-id="oauth2-widget-link">
                  {chunk}
                </LinkPAStyle>
              ),
              c: (chunk: string) => (
                <LinkPAStyle
                  href={OAUTH2API}
                  target="_blank"
                  rel="noreferrer"
                  data-test-id="oauth2-api-link">
                  {chunk}
                </LinkPAStyle>
              )
            }
          )}
        />
      </ModalContentBox>
      <div style={{ marginBottom: '12px' }}>
        <CopyHiddenInput
          type="text"
          title={intl.messages['authorization.settings.oauth2.modal.connect.success.client-id']}
          value={oauthSettings.id}
          dataTestId="oauth2-client-id"
        />
      </div>
      <div style={{ marginBottom: '24px' }}>
        <CopyHiddenInput
          title={intl.messages['authorization.settings.oauth2.server.secret-key.title']}
          value={oauthSettings.secret}
          dataTestId="oauth2-client-secret"
        />
      </div>
      <Form
        onSubmit={onSubmit}
        initialValues={{
          name: oauthSettings.name,
          redirect_uris: oauthSettings.redirect_uris,
          is_public: oAuthTypeMemo,
          lifetime: oauthSettings.lifetime
        }}
        validate={Oauth2ModalValidator}
        mutators={{ ...arrayMutators }}>
        {({ handleSubmit, hasValidationErrors, pristine, errors, values: { lifetime } }) => {
          return (
            <form onSubmit={handleSubmit}>
              <ModalContentBox>
                <FieldInput
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                  label={intl.messages['authorization.settings.oauth2.table.client-name']}
                  name="name"
                  errorMessage={
                    errors?.name &&
                    intl.formatMessage(
                      {
                        id: errors?.name
                      },
                      {
                        number: clientNameMaxLength
                      }
                    )
                  }
                  placeholder={intl.formatMessage({
                    id: 'authorization.settings.oauth2.server.name.placeholder'
                  })}
                  data-test-id="oauth2-client-name"
                />
              </ModalContentBox>
              <ModalContentBox>
                <FieldInputNumber
                  name="lifetime"
                  hideArrows
                  subLabelCompact
                  label={intl.formatMessage({ id: 'settings.signature.token-lifetime.title' })}
                  errorMessage={
                    errors?.lifetime
                      ? intl.formatMessage(
                          { id: `${errors?.lifetime}` },
                          {
                            number:
                              `${errors?.lifetime}` === 'app.validation.value-more'
                                ? 1893456000
                                : 300
                          }
                        )
                      : ''
                  }
                  subLabel={
                    // eslint-disable-next-line react/jsx-wrap-multilines
                    <FormattedMessage
                      id="settings.signature.token-lifetime.description-oauth"
                      values={{
                        br: () => <br />
                      }}
                    />
                  }
                  extra={() => (
                    <TokenExtra>
                      {intl.formatMessage({ id: 'settings.signature.token-lifetime.seconds' })}
                    </TokenExtra>
                  )}
                  extraAlign="right"
                />
              </ModalContentBox>
              {lifetime > 432000 && (
                <ModalContentBox>
                  <Checkbox
                    checked={agreeLongToken}
                    disabled={fetching}
                    onChange={handleChangeCheck}>
                    <HeavyFont>
                      {intl.formatMessage({ id: 'settings.signature.long-token-checkbox' })}
                    </HeavyFont>
                  </Checkbox>
                </ModalContentBox>
              )}
              <Field name="is_public" type="radio">
                {(props) => (
                  <RadioGroup
                    disabled
                    name={props.input.name}
                    onChange={props.input.onChange}
                    defaultValue={oAuthTypeMemo}
                    options={[
                      {
                        label: intl.formatMessage(
                          {
                            id: 'authorization.settings.oauth2.modal.connect.type.public'
                          },
                          {
                            span: (chunk: string) => <CommonFont>{chunk}</CommonFont>
                          }
                        ),
                        radioValue: ClientTypes.public,
                        description: intl.formatMessage({
                          id: 'authorization.settings.oauth2.modal.connect.type.public.description'
                        }),
                        dataTestId: 'oauth2-type-public'
                      },
                      {
                        label: intl.formatMessage(
                          {
                            id: 'authorization.settings.oauth2.modal.connect.type.private'
                          },
                          {
                            span: (chunk: string) => <CommonFont>{chunk}</CommonFont>
                          }
                        ),
                        radioValue: ClientTypes.confidential,
                        description: intl.formatMessage({
                          id: 'authorization.settings.oauth2.modal.connect.type.confidential.description'
                        }),
                        dataTestId: 'oauth2-type-confidential'
                      },
                      {
                        label: intl.formatMessage(
                          {
                            id: 'authorization.settings.oauth2.modal.connect.type.server'
                          },
                          {
                            span: (chunk: string) => <CommonFont>{chunk}</CommonFont>
                          }
                        ),
                        radioValue: ClientTypes.server,
                        description: intl.formatMessage({
                          id: 'authorization.settings.oauth2.modal.connect.type.server.description'
                        }),
                        dataTestId: 'oauth2-type-server'
                      }
                    ]}
                  />
                )}
              </Field>
              {oAuthTypeMemo !== 'server' && (
                <ModalContentBoxToken>
                  <LinkFieldArray
                    name="redirect_uris"
                    label={intl.formatMessage({
                      id: 'authorization.settings.oauth2.modal.connect.redirect-uri.title'
                    })}
                    addLabel={intl.formatMessage({
                      id: 'configuration-page.urls.callback-urls.input.add-button'
                    })}
                    placeholder={intl.formatMessage({
                      id: 'authorization.settings.oauth2.modal.connect.redirect-uri.placeholder'
                    })}
                  />
                </ModalContentBoxToken>
              )}
              <RemoveButtonBlock>
                <Button
                  data-test-id="oauth2-delete-button"
                  look="link"
                  palette="danger"
                  size="md"
                  onClick={() => {
                    openRemoveModal();
                    onClose();
                  }}>
                  {intl.messages['authorization.settings.oauth2.modal.connect.update.remove']}
                </Button>
                <RemoveButtonSubtitle>
                  {
                    intl.messages[
                      'authorization.settings.oauth2.modal.connect.update.remove.subtitle'
                    ]
                  }
                </RemoveButtonSubtitle>
              </RemoveButtonBlock>

              <FormFooter
                isModal
                onSubmit={() => onSubmit}
                onCancel={onClose}
                fetching={fetching}
                error={error}
                disabled={pristine || (lifetime > 432000 && !agreeLongToken)}
              />
            </form>
          );
        }}
      </Form>
    </Modal>
  );
};
